// Kit dashboard bar — the single, compact Kit surface.
//
// One slim bar:
//   • Left   — greeting + summary ("Good morning Steve — 4 things need…")
//   • Middle — clickable category badges ("2 leads", "1 payment", …)
//   • Right  — "Ask Kit" button (opens the Kit panel)
// Collapsed by default; clicking a badge expands that category's alert cards
// below the bar. Clicking again / another badge collapses it.
//
// Source of truth: the persisted kit_alerts (GET /api/kit/actions/alerts/active),
// written by the backend monitor — same data the Kit panel renders, so an alert
// never shows twice and dismiss/snooze sticks. Each card carries a one-click
// action (mark actioned + deep-link) and a dismiss menu (handled / not relevant
// / snooze 24h). Replaces the previous ephemeral proactive-actions + briefing
// cards (recomputed each load, session-only dismiss).

import { iconMarkup, type IconName } from "@tkos/shared-ui";
import { openKit } from "../../shell/kit-panel";
import { fetchActiveAlerts, renderAlertCard, esc, type KitAlert } from "../../shell/kit-alerts";
import * as realtime from "../../shell/realtime";
import "./kit-bar.css";

interface Family {
  key: string;
  icon: IconName;
  order: number;
  noun: (n: number) => string;
  match: (type: string) => boolean;
}

// Display families, in the spec's urgency order (payments → deliveries →
// messages → leads → quotes → snags).
const FAMILIES: Family[] = [
  { key: "payments",   icon: "pound",          order: 0, noun: (n) => `${n} payment${n === 1 ? "" : "s"}`,
    match: (t) => /deposit|balance|payment|no-deposit/.test(t) },
  { key: "deliveries", icon: "truck",          order: 1, noun: (n) => `${n} delivery${n === 1 ? "" : " items"}`,
    match: (t) => t.startsWith("delivery") },
  { key: "messages",   icon: "chat",           order: 2, noun: (n) => `${n} message${n === 1 ? "" : "s"}`,
    match: (t) => t.includes("message") && !t.startsWith("competitor") },
  { key: "competitors", icon: "chart",          order: 3, noun: (n) => `${n} competitor mention${n === 1 ? "" : "s"}`,
    match: (t) => t.startsWith("competitor") },
  { key: "leads",      icon: "customer",       order: 3, noun: (n) => `${n} lead${n === 1 ? "" : "s"}`,
    match: (t) => t.startsWith("lead") },
  { key: "quotes",     icon: "file",           order: 4, noun: (n) => `${n} quote${n === 1 ? "" : "s"}`,
    match: (t) => t.startsWith("quote") },
  { key: "snags",      icon: "alert-triangle", order: 5, noun: (n) => `${n} snag${n === 1 ? "" : "s"}`,
    match: (t) => t.startsWith("snag") },
];

function familyOf(type: string): Family | null {
  return FAMILIES.find((f) => f.match(type)) || null;
}
function greeting(name: string): string {
  const h = new Date().getHours();
  return `${h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening"} ${name}`.trim();
}

export function mountKitBar(host: HTMLElement, retailerId: string): () => void {
  let destroyed = false;
  let alerts: KitAlert[] = [];
  let openKey: string | null = null;
  host.innerHTML = "";

  async function refresh(): Promise<void> {
    const next = await fetchActiveAlerts(retailerId);
    if (destroyed) return;
    alerts = next;
    build();
  }
  void refresh();

  // Live-refresh when the monitor broadcasts a change.
  const offKitAlert = realtime.onEvent("kit.alert", () => { void refresh(); });

  function build(): void {
    if (destroyed) return;

    // Group live alerts by family, preserving urgency-sorted order within.
    const groups = new Map<string, KitAlert[]>();
    for (const a of alerts) {
      const fam = familyOf(a.type);
      if (!fam) continue;
      if (!groups.has(fam.key)) groups.set(fam.key, []);
      groups.get(fam.key)!.push(a);
    }
    const cats = FAMILIES
      .filter((f) => groups.has(f.key))
      .sort((a, b) => a.order - b.order);

    const total = alerts.length;
    if (total === 0 || cats.length === 0) { host.innerHTML = ""; return; }
    if (openKey && !cats.some((c) => c.key === openKey)) openKey = null;

    host.innerHTML = `
      <div class="kit-bar__row">
        <div class="kit-bar__lead">
          <span class="kit-bar__badge-kit">${iconMarkup("sparkle", { size: "sm" })} Kit</span>
          <span class="kit-bar__greet">${esc(greeting(""))} — ${total} thing${total === 1 ? "" : "s"} need${total === 1 ? "s" : ""} your attention</span>
        </div>
        <div class="kit-bar__badges" data-role="badges">
          ${cats.map((c) => `
            <button type="button" class="kit-bar__cat${c.key === openKey ? " is-open" : ""}" data-cat="${esc(c.key)}">
              ${iconMarkup(c.icon, { size: "xs" })} ${esc(c.noun(groups.get(c.key)!.length))}
            </button>`).join("")}
        </div>
        <button type="button" class="kit-bar__ask" data-role="ask">Ask Kit ${iconMarkup("arrow-right", { size: "xs" })}</button>
      </div>
      <div class="kit-bar__panel" data-role="panel" ${openKey ? "" : "hidden"}></div>`;

    host.querySelector<HTMLButtonElement>('[data-role="ask"]')!.addEventListener("click", () => openKit());
    host.querySelectorAll<HTMLButtonElement>("[data-cat]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const key = btn.dataset["cat"]!;
        openKey = openKey === key ? null : key;
        build();
      });
    });

    const panel = host.querySelector<HTMLElement>('[data-role="panel"]');
    if (panel && openKey) {
      const items = groups.get(openKey) || [];
      const stack = document.createElement("div");
      stack.className = "kit-bar__cards";
      panel.appendChild(stack);
      for (const a of items) {
        renderAlertCard(stack, a, retailerId, (id) => {
          alerts = alerts.filter((x) => x.id !== id);
          build();
        });
      }
    }
  }

  return () => { destroyed = true; offKitAlert(); host.innerHTML = ""; };
}
